import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { Platform, TextInput } from 'react-native';

const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/** Height that shows every line of a textarea: its scrollHeight, never below minHeight. */
export function autoGrowHeight(scrollHeight, minHeight = 0) {
  const h = Number(scrollHeight);
  const min = Number(minHeight) || 0;
  return Number.isFinite(h) && h > min ? h : min;
}

/**
 * Multiline TextInput that grows with its content on web, so it never shows its own
 * scrollbar (REQ-101: the LIVE spec sheet fields). The textarea is resized from its
 * scrollHeight whenever the value or the window width changes. Native keeps the stock
 * multiline behaviour, which already grows.
 */
export function AutoGrowTextInput({ style, value, minHeight = 24, ...rest }) {
  const ref = useRef(null);

  const resize = useCallback(() => {
    if (Platform.OS !== 'web') return;
    const node = ref.current;
    if (!node || !node.style) return;
    node.style.height = 'auto';
    // border-box: add the top/bottom borders so the last line is not 1px short.
    const borders = (node.offsetHeight || 0) - (node.clientHeight || 0);
    node.style.height = `${autoGrowHeight(node.scrollHeight + Math.max(0, borders), minHeight)}px`;
  }, [minHeight]);

  useIsoLayoutEffect(() => {
    resize();
  }, [value, resize]);

  // Re-measure when the field's width changes (window resize, the expense rail opening,
  // being shown after mounting hidden) and once the web font has loaded.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return undefined;
    let cancelled = false;
    const node = ref.current;
    let observer = null;
    if (node && typeof window.ResizeObserver === 'function') {
      observer = new window.ResizeObserver(() => resize());
      observer.observe(node);
    } else {
      window.addEventListener('resize', resize);
    }
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(() => { if (!cancelled) resize(); }).catch(() => {});
    }
    return () => {
      cancelled = true;
      if (observer) observer.disconnect();
      else window.removeEventListener('resize', resize);
    };
  }, [resize]);

  return (
    <TextInput
      ref={ref}
      multiline
      value={value}
      rows={1}
      style={[
        style,
        { minHeight },
        Platform.OS === 'web' ? { overflow: 'hidden', resize: 'none' } : null,
      ]}
      {...rest}
    />
  );
}

export default AutoGrowTextInput;
